// client/src/pages/profile/Profile.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api, { fieldErrorsFrom } from '../../services/api';
import openProtectedFile from '../../utils/openProtectedFile';
import toast from 'react-hot-toast';
import computeCompletion from '../../components/profile/completion';
import { formatRole } from '../../components/common/navigation';
import VerificationCard from '../../components/profile/VerificationCard';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  DetailRow,
  Divider,
  EmptyState,
  IconButton,
  Input,
  Modal,
  PageHeader,
  PageShell,
  SectionBlock,
  Select,
  Skeleton,
  SkeletonCard,
  Tabs,
  Textarea
} from '../../components/ui';
import {
  UserCircleIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  TrophyIcon,
  DocumentTextIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';

/** Achievement type → badge tone. */
const ACHIEVEMENT_TONES = {
  academic: 'info',
  project: 'success',
  certification: 'purple',
  competition: 'warning',
  publication: 'danger',
  other: 'neutral'
};

// A school student's "year" is their class. The API accepts 1-12; the college
// list stopped at 6th year, so a Class 11 or 12 student could not say so.
const SCHOOL_CLASS_OPTIONS = [
  { value: '', label: 'Select class' },
  ...Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `Class ${i + 1}` }))
];

const YEAR_OPTIONS = [
  { value: '', label: 'Select year' },
  { value: '1', label: '1st year' },
  { value: '2', label: '2nd year' },
  { value: '3', label: '3rd year' },
  { value: '4', label: '4th year' },
  { value: '5', label: '5th year' },
  { value: '6', label: '6th year' }
];

/**
 * Academic fields the placement cell locks once it has verified them. Mirrors
 * the server's list (userController); the server is what enforces it, this
 * only keeps the form honest about what can be changed.
 */
const LOCKED_ACADEMIC_FIELDS = [
  'studentId',
  'course',
  'branch',
  'yearOfStudy',
  'graduationYear',
  'cgpa',
  'activeBacklogs',
  'totalBacklogs',
  'class10Percentage',
  'class12Percentage',
  'diplomaPercentage',
  'educationGapYears',
  'percentage'
];

/** Plain labels for profile fields, for error messages and the locked list. */
const FIELD_LABELS = {
  firstName: 'First name',
  lastName: 'Last name',
  phone: 'Phone',
  studentId: 'Roll number',
  dateOfBirth: 'Date of birth',
  course: 'Course',
  branch: 'Branch',
  yearOfStudy: 'Year of study',
  graduationYear: 'Graduation year',
  cgpa: 'CGPA',
  activeBacklogs: 'Active backlogs',
  totalBacklogs: 'Total backlogs',
  class10Percentage: 'Class X %',
  class12Percentage: 'Class XII %',
  diplomaPercentage: 'Diploma %',
  educationGapYears: 'Gap in education',
  percentage: 'Percentage',
  linkedinUrl: 'LinkedIn URL',
  githubUrl: 'GitHub URL',
  portfolioUrl: 'Portfolio URL'
};

const PERSONAL_FIELDS = ['firstName', 'lastName', 'phone', 'dateOfBirth', 'gender', 'address', 'bio', 'linkedinUrl', 'githubUrl', 'portfolioUrl'];

/** "8,5" is how a comma-decimal keyboard types 8.5. */
const toNumber = (value) => Number(String(value).trim().replace(',', '.'));

/** Add https:// to a bare "linkedin.com/in/…" so the saved link actually opens. */
const normaliseUrl = (value) => {
  const v = String(value || '').trim();
  if (!v) return '';
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

/**
 * Check the form before it is sent, field by field, in words a student can
 * act on. The server's own answers were "Invalid value" and "cgpa is not
 * valid", and some bad input was accepted silently — "8,5" saved as a CGPA
 * of 8, and an impossible graduation year went through.
 */
const validateProfile = (data, isStudent) => {
  const errors = {};
  if (!String(data.firstName || '').trim()) errors.firstName = 'Enter your first name.';
  if (!String(data.lastName || '').trim()) errors.lastName = 'Enter your last name.';
  const phone = String(data.phone || '').trim();
  if (phone) {
    const digits = phone.replace(/[\s()-]/g, '');
    if (!/^\+?\d{10,15}$/.test(digits) && !/^0\d{10}$/.test(digits)) {
      errors.phone = 'Enter a 10-digit mobile number, like 98765 43210 or +91 98765 43210.';
    }
  }
  if (!isStudent) return errors;

  const number = (field, { min, max, integer, message }) => {
    const raw = data[field];
    if (raw === '' || raw === null || raw === undefined) return;
    const n = toNumber(raw);
    if (!Number.isFinite(n) || n < min || n > max || (integer && !Number.isInteger(n))) {
      errors[field] = message;
    }
  };
  number('cgpa', { min: 0, max: 10, message: 'Enter your CGPA out of 10, like 8.5.' });
  number('class10Percentage', { min: 0, max: 100, message: 'Enter a percentage between 0 and 100, like 86.4.' });
  number('class12Percentage', { min: 0, max: 100, message: 'Enter a percentage between 0 and 100, like 78.2.' });
  number('diplomaPercentage', { min: 0, max: 100, message: 'Enter a percentage between 0 and 100.' });
  number('activeBacklogs', { min: 0, max: 100, integer: true, message: 'Enter a whole number, 0 if you have none.' });
  number('educationGapYears', { min: 0, max: 20, integer: true, message: 'Enter the gap in whole years, 0 if none.' });
  const thisYear = new Date().getFullYear();
  number('graduationYear', {
    min: thisYear - 10,
    max: thisYear + 6,
    integer: true,
    message: `Enter the year you finish your course, between ${thisYear - 10} and ${thisYear + 6}.`
  });
  if (data.dateOfBirth) {
    const dob = new Date(data.dateOfBirth);
    if (Number.isNaN(dob.getTime()) || dob > new Date() || dob.getFullYear() < 1950) {
      errors.dateOfBirth = 'Pick your date of birth from the calendar.';
    }
  }
  ['linkedinUrl', 'githubUrl', 'portfolioUrl'].forEach((field) => {
    const v = normaliseUrl(data[field]);
    if (!v) return;
    try {
      const host = new URL(v).hostname;
      if (!host.includes('.')) throw new Error('no host');
    } catch {
      errors[field] = 'Enter a web address, like linkedin.com/in/your-name.';
    }
  });
  return errors;
};

/** True for a resume stored on our own server rather than an external link. */
const isStoredFile = (url) => typeof url === 'string' && (url.includes('/uploads/') || !/^https?:\/\//i.test(url));

const Profile = () => {
  const { id } = useParams(); // Get user ID from URL params
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [achievements, setAchievements] = useState([]);
  // Which organisations have actually seen this profile. Recruiter access is
  // already scoped and already logged; this is the student's own view of it.
  const [dataAccess, setDataAccess] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  // Fields the server refused with 423 Locked on the last save.
  const [lockedFieldsHit, setLockedFieldsHit] = useState([]);
  // Per-field messages from the last save attempt (client check or server).
  const [errors, setErrors] = useState({});
  const [deletingAchievement, setDeletingAchievement] = useState(null);
  // Why the profile could not be loaded: 'not_found' (404, or 403 — the API
  // answers both for a profile this viewer may not see) or 'error'. Without it
  // the page rendered an empty "Profile, completion 0%" shell.
  const [loadError, setLoadError] = useState(null);
  // The route param is a string and `user.id` a number; compared strictly,
  // a student opening their own /users/:id link counted as "someone else".
  const isViewingOtherUser = Boolean(id) && String(id) !== String(user?.id);
  const [formData, setFormData] = useState({
    // Personal Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Student Profile
    studentId: '',
    dateOfBirth: '',
    gender: '',
    course: '',
    branch: '',
    yearOfStudy: '',
    graduationYear: '',
    cgpa: '',
    activeBacklogs: '',
    class10Percentage: '',
    class12Percentage: '',
    diplomaPercentage: '',
    educationGapYears: '',
    percentage: '',
    address: '',
    skills: [],
    bio: '',
    linkedinUrl: '',
    githubUrl: '',
    portfolioUrl: ''
  });
  const [newSkill, setNewSkill] = useState('');
  const [newAchievement, setNewAchievement] = useState({
    title: '',
    description: '',
    achievementType: 'academic',
    issuingOrganization: '',
    issueDate: '',
    credentialUrl: ''
  });
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [addingAchievement, setAddingAchievement] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      // If viewing another user's profile, use /users/:id, otherwise use /users/profile
      const endpoint = id ? `/users/${id}` : '/users/profile';
      // Silent: the failure is explained on the page itself below.
      const response = await api.get(endpoint, { silent: true });
      const userData = response.user || response; // Handle different response formats
      setProfile(userData);

      setFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phone: userData.phone || '',
        studentId: userData.studentProfile?.studentId || '',
        dateOfBirth: userData.studentProfile?.dateOfBirth || '',
        gender: userData.studentProfile?.gender || '',
        course: userData.studentProfile?.course || '',
        branch: userData.studentProfile?.branch || '',
        yearOfStudy: userData.studentProfile?.yearOfStudy || '',
        graduationYear: userData.studentProfile?.graduationYear || '',
        cgpa: userData.studentProfile?.cgpa || '',
        activeBacklogs: userData.studentProfile?.activeBacklogs ?? '',
        class10Percentage: userData.studentProfile?.class10Percentage ?? '',
        class12Percentage: userData.studentProfile?.class12Percentage ?? '',
        diplomaPercentage: userData.studentProfile?.diplomaPercentage ?? '',
        educationGapYears: userData.studentProfile?.educationGapYears ?? '',
        percentage: userData.studentProfile?.percentage || '',
        address: userData.studentProfile?.address || '',
        skills: userData.studentProfile?.skills || [],
        bio: userData.studentProfile?.bio || '',
        linkedinUrl: userData.studentProfile?.linkedinUrl || '',
        githubUrl: userData.studentProfile?.githubUrl || '',
        portfolioUrl: userData.studentProfile?.portfolioUrl || ''
      });

      setAchievements(userData.achievements || []);
    } catch (error) {
      const status = error?.status || error?.response?.status;
      setProfile(null);
      setLoadError(status === 404 || status === 403 ? 'not_found' : 'error');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear a field's message as soon as it is being fixed.
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  /** Open the tab holding the first field that needs fixing. */
  const showFirstError = (fieldErrors) => {
    const first = Object.keys(fieldErrors).find((k) => fieldErrors[k]);
    if (!first) return;
    setActiveTab(PERSONAL_FIELDS.includes(first) ? 'personal' : 'academic');
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData((prev) => ({ ...prev, skills: [...prev.skills, newSkill.trim()] }));
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.filter((skill) => skill !== skillToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    const clientErrors = validateProfile(formData, user.role === 'student');
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      showFirstError(clientErrors);
      // One id, so a second failed attempt replaces the toast and a later
      // successful save can take it down instead of leaving it beside
      // "Profile updated successfully".
      toast.error('Some details need fixing. Check the highlighted fields.', { id: 'profile-validation' });
      return;
    }
    setErrors({});
    try {
      setIsSaving(true);
      const { firstName, lastName, phone, ...profileData } = formData;
      // `email` is read-only server-side, so it is destructured out and dropped.
      delete profileData.email;

      const updateData = { firstName, lastName, phone };

      // Add profile data for students
      if (user.role === 'student') {
        // An untouched field is '' in the form, which is not a valid date,
        // gender, URL or number. Send null ("not set") instead.
        for (const [key, value] of Object.entries(profileData)) {
          // A locked field cannot change, so it is not sent at all: the form
          // holds "8.5" where the database holds "8.50", and resending it
          // could be read as an edit.
          if (academicLocked && LOCKED_ACADEMIC_FIELDS.includes(key)) continue;
          if (value === '' || value === null) {
            updateData[key] = null;
          } else if (['linkedinUrl', 'githubUrl', 'portfolioUrl'].includes(key)) {
            updateData[key] = normaliseUrl(value);
          } else if (
            ['cgpa', 'class10Percentage', 'class12Percentage', 'diplomaPercentage'].includes(key)
          ) {
            updateData[key] = toNumber(value);
          } else if (key === 'studentId') {
            // Roll numbers are compared case-insensitively by the cell's
            // shortlist paste; stored as typed, minus stray spaces.
            updateData[key] = String(value).trim();
          } else {
            updateData[key] = value;
          }
        }
      }

      const response = await api.put('/users/profile', updateData);

      // Merged: the save response does not carry achievements or every
      // association the page loaded.
      setProfile((prev) => ({ ...prev, ...response.user }));
      updateUser(response.user);
      setIsEditing(false);
      setLockedFieldsHit([]);
      toast.dismiss('profile-validation');
      toast.success('Profile updated successfully');
    } catch (error) {
      // 423: the placement cell verified these fields and they are locked.
      // The api interceptor has already toasted the server's message; keep
      // the list of offending fields on screen so the student knows which.
      if (error.status === 423) {
        setLockedFieldsHit(error.data?.lockedFields || []);
        setActiveTab('academic');
      }
      // A validation answer: put each message under its own field. The
      // server's wording is often just "Invalid value", so fall back to
      // naming the field.
      const serverErrors = fieldErrorsFrom(error);
      (error.data?.details || []).forEach((d) => {
        const field = d?.field || d?.path;
        if (field && !serverErrors[field]) {
          serverErrors[field] = `Check your ${(FIELD_LABELS[field] || field).toLowerCase()}.`;
        }
      });
      Object.keys(serverErrors).forEach((field) => {
        if (/is not valid$|^Invalid value$/.test(serverErrors[field])) {
          serverErrors[field] = `Check your ${(FIELD_LABELS[field] || field).toLowerCase()}.`;
        }
      });
      if (Object.keys(serverErrors).length) {
        setErrors(serverErrors);
        showFirstError(serverErrors);
      }
      // The api interceptor has already toasted the overall message.
    } finally {
      setIsSaving(false);
    }
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }

    const body = new FormData();
    body.append('profilePicture', file);

    const loadingToast = toast.loading('Uploading profile picture...');
    try {
      const response = await api.post('/users/profile/picture', body, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.dismiss(loadingToast);
      toast.success('Profile picture updated successfully');

      const picture = response.data?.profilePicture || response.profilePicture;
      setProfile((prev) => ({ ...prev, profilePicture: picture }));
      updateUser({ ...user, profilePicture: picture });
    } catch (error) {
      // The api interceptor has already shown why (too large, wrong type).
      toast.dismiss(loadingToast);
    }
  };

  const handleAddAchievement = async () => {
    try {
      setAddingAchievement(true);
      const achievementData = {
        title: newAchievement.title.trim(),
        achievementType: newAchievement.achievementType
      };

      // Add optional fields only if they have values
      if (newAchievement.description && newAchievement.description.trim()) {
        achievementData.description = newAchievement.description.trim();
      }

      if (newAchievement.issuingOrganization && newAchievement.issuingOrganization.trim()) {
        achievementData.issuingOrganization = newAchievement.issuingOrganization.trim();
      }

      // The field is a native date input, so it normally arrives as YYYY-MM-DD;
      // anything else is assumed to be DD-MM-YYYY and flipped.
      if (newAchievement.issueDate && newAchievement.issueDate.trim()) {
        const dateInput = newAchievement.issueDate.trim();
        if (dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
          achievementData.issueDate = dateInput;
        } else {
          const dateParts = dateInput.split('-');
          if (dateParts.length === 3) {
            achievementData.issueDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
          }
        }
      }

      if (newAchievement.credentialUrl && newAchievement.credentialUrl.trim()) {
        let url = newAchievement.credentialUrl.trim();
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }
        achievementData.credentialUrl = url;
      }

      const response = await api.post('/achievements', achievementData);
      setAchievements((prev) => [response.achievement, ...prev]);
      setNewAchievement({
        title: '',
        description: '',
        achievementType: 'academic',
        issuingOrganization: '',
        issueDate: '',
        credentialUrl: ''
      });
      setShowAchievementModal(false);
      toast.success('Achievement added successfully');
    } catch (error) {
      // The api interceptor has already shown the server's reason; this used
      // to add a second toast reading "Validation error: undefined: undefined"
      // because the details are `{ field, message }`, not `{ path, msg }`.
    } finally {
      setAddingAchievement(false);
    }
  };

  const handleDeleteAchievement = async () => {
    const achievementId = deletingAchievement?.id;
    if (!achievementId) return;
    try {
      await api.delete(`/achievements/${achievementId}`);
      setAchievements((prev) => prev.filter((a) => a.id !== achievementId));
      toast.success('Achievement deleted');
    } catch (error) {
      // Toasted by the api interceptor.
    } finally {
      setDeletingAchievement(null);
    }
  };

  // Resumes are fetched through the authenticated file route — the public
  // /uploads folder no longer serves them, and a bare link cannot carry the
  // Bearer token.
  const resumeSource = () =>
    profile?.studentProfile?.resumeFileId
      ? `/files/${profile.studentProfile.resumeFileId}/download`
      : profile?.studentProfile?.resumeUrl;

  const handleViewResume = () => openProtectedFile(resumeSource());

  const handleDownloadResume = async () => {
    const ok = await openProtectedFile(resumeSource(), {
      download: true,
      filename: `${profile.firstName}_${profile.lastName}_Resume.pdf`
    });
    if (ok) toast.success('Resume download started');
  };

  const isStudentProfile = profile?.role === 'student';
  // The profile owner's school, not the viewer's: a school student has a class,
  // not a year of a degree, and no roll number, CGPA, backlogs or graduating
  // batch for a placement cell to check.
  const isSchoolStudent = isStudentProfile && profile?.organization?.type === 'school';
  const yearOptions = isSchoolStudent ? SCHOOL_CLASS_OPTIONS : YEAR_OPTIONS;
  // Once the placement cell has verified the academic record the server
  // refuses changes to it (423); show those fields read-only instead.
  const academicLocked =
    isStudentProfile && profile?.studentProfile?.verificationStatus === 'verified';

  useEffect(() => {
    // Fetched only when the tab is opened: most visits never ask this, and it
    // reads a broad slice of the audit table.
    if (activeTab !== 'privacy' || dataAccess || isViewingOtherUser) return;
    api
      .get('/audit/my-data', { silent: true })
      .then((res) => setDataAccess(res.entries || []))
      .catch(() => setDataAccess([]));
  }, [activeTab, dataAccess, isViewingOtherUser]);


  const tabs = useMemo(
    () => [
      { value: 'personal', label: 'Personal', icon: UserCircleIcon },
      ...(isStudentProfile
        ? [
            { value: 'academic', label: 'Academic', icon: AcademicCapIcon },
            { value: 'achievements', label: 'Achievements', icon: TrophyIcon, count: achievements.length },
            { value: 'resume', label: 'Resume', icon: DocumentTextIcon },
            // The student's own access log. Staff viewing a student got this
            // tab too, labelled "my profile", showing their own audit data.
            ...(isViewingOtherUser
              ? []
              : [{ value: 'privacy', label: 'Who saw my profile', icon: ShieldCheckIcon }])
          ]
        : [])
    ],
    [isStudentProfile, isViewingOtherUser, achievements.length]
  );

  if (isLoading) {
    return (
      <PageShell width="narrow">
        <Skeleton className="h-10 w-64" />
        <div className="mt-8 space-y-4">
          <SkeletonCard lines={2} />
          <SkeletonCard lines={6} />
        </div>
      </PageShell>
    );
  }

  if (loadError || !profile) {
    const notFound = loadError === 'not_found';
    return (
      <PageShell width="narrow">
        <EmptyState
          icon={notFound ? ShieldCheckIcon : UserCircleIcon}
          title={notFound ? 'This profile is not available' : 'Could not load this profile'}
          description={
            notFound
              ? 'It does not exist, or your account is not allowed to see it.'
              : 'Check your connection and try again.'
          }
          action={
            notFound ? (
              <Button as={Link} to="/dashboard">
                Back to dashboard
              </Button>
            ) : (
              <Button onClick={fetchProfile}>Try again</Button>
            )
          }
        />
      </PageShell>
    );
  }

  // The owner's role, not the viewer's: a TPO opening a student's profile was
  // scored as a TPO. Achievements from state, which add/delete keep current.
  const profileCompletion = profile ? computeCompletion({ ...profile, achievements }) : 0;
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ');
  const studentProfile = profile?.studentProfile;
  const hasResume = Boolean(studentProfile?.resumeUrl || studentProfile?.resumeFileId);

  return (
    <PageShell width="narrow">
      <PageHeader
        eyebrow={isViewingOtherUser ? 'Student profile' : 'Account'}
        title={fullName || 'Profile'}
        lead={[formatRole(profile?.role), profile?.organization?.name]
          .filter(Boolean)
          .join(' · ')}
        actions={
          !isViewingOtherUser && (
            <Button
              variant={isEditing ? 'secondary' : 'primary'}
              icon={isEditing ? XMarkIcon : PencilIcon}
              onClick={() => {
                setIsEditing(!isEditing);
                setErrors({});
              }}
            >
              {isEditing ? 'Cancel editing' : 'Edit profile'}
            </Button>
          )
        }
      />

      <Card className="mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <Avatar src={profile?.profilePicture} name={fullName} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="font-display text-lg font-bold text-ink-950">{fullName}</p>
            <p className="text-sm text-ink-600">{formatRole(profile?.role)}</p>
            {profile?.organization?.name && (
              <p className="text-sm text-ink-500">{profile.organization.name}</p>
            )}
          </div>
          {profileCompletion === 100 ? (
            // Its own row on a phone, like the progress bar below: squeezed
            // beside the avatar it wrapped the name onto two lines.
            <div className="w-full sm:w-auto">
              <Badge tone="success" dot>
                Profile complete
              </Badge>
            </div>
          ) : (
            <div className="w-full sm:w-56">
              <div className="flex items-center justify-between text-xs">
                <span className="uppercase tracking-[0.07em] text-ink-500">
                  Completion
                </span>
                <span className="font-semibold tabular-nums text-ink-800">
                  {profileCompletion}%
                </span>
              </div>
              <div
                role="progressbar"
                aria-label="Profile completion"
                aria-valuenow={profileCompletion}
                aria-valuemin={0}
                aria-valuemax={100}
                className="mt-2 h-2 w-full overflow-hidden rounded-full bg-bone-200"
              >
                <div
                  className="h-full rounded-full bg-saffron-500 transition-all duration-300"
                  style={{ width: `${profileCompletion}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {isEditing && !isViewingOtherUser && (
          <>
            <Divider className="my-5" />
            <label
              htmlFor="profile-picture"
              className="mb-1.5 block text-sm font-medium text-ink-800"
            >
              Update profile picture
            </label>
            <input
              id="profile-picture"
              type="file"
              accept="image/*"
              onChange={handleProfilePictureChange}
              className="block w-full text-sm text-ink-600 file:mr-4 file:rounded-full file:border file:border-ink-950 file:bg-ink-950 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-ink-800"
            />
            <p className="mt-1.5 text-xs text-ink-500">JPG or PNG, up to 5MB.</p>
          </>
        )}
      </Card>

      {isStudentProfile && !isSchoolStudent && !isViewingOtherUser && studentProfile && (
        <VerificationCard profile={studentProfile} onChange={fetchProfile} />
      )}

      <Tabs className="mb-6 w-fit max-w-full" tabs={tabs} value={activeTab} onChange={setActiveTab} />

      {isEditing && !isViewingOtherUser ? (
        // noValidate: the browser's own bubble ("Value must be less than
        // or equal to 10") only fires for the tab on screen and says nothing
        // a student can act on; validateProfile checks every tab in words.
        <form onSubmit={handleSubmit} noValidate>
          {activeTab === 'personal' && (
            <Card className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <Input
                  label="First name"
                  name="firstName"
                  error={errors.firstName}
                  value={formData.firstName}
                  onChange={handleInputChange}
                />
                <Input
                  label="Last name"
                  name="lastName"
                  error={errors.lastName}
                  value={formData.lastName}
                  onChange={handleInputChange}
                />
              </div>
              <Input
                label="Email"
                type="email"
                name="email"
                error={errors.email}
                value={formData.email}
                disabled
                help="Your sign-in email cannot be changed here."
              />
              <Input
                label="Phone"
                type="tel"
                name="phone"
                error={errors.phone}
                inputMode="tel"
                placeholder="98765 43210"
                help="Your 10-digit mobile number. Recruiters and your placement cell use it to reach you."
                value={formData.phone}
                onChange={handleInputChange}
              />

              {isStudentProfile && (
                <>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Input
                      label="Date of birth"
                      type="date"
                      name="dateOfBirth"
                      error={errors.dateOfBirth}
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                    />
                    <Select
                      label="Gender"
                      name="gender"
                      error={errors.gender}
                      value={formData.gender}
                      onChange={handleInputChange}
                      options={[
                        { value: '', label: 'Select gender' },
                        { value: 'male', label: 'Male' },
                        { value: 'female', label: 'Female' },
                        { value: 'other', label: 'Other' }
                      ]}
                    />
                  </div>
                  <Textarea
                    label="Address"
                    rows={3}
                    name="address"
                    error={errors.address}
                    value={formData.address}
                    onChange={handleInputChange}
                  />
                  <Textarea
                    label="Bio"
                    rows={4}
                    name="bio"
                    error={errors.bio}
                    value={formData.bio}
                    onChange={handleInputChange}
                    placeholder="Tell us about yourself…"
                  />
                  <Divider />
                  <h2 className="font-display text-base font-bold text-ink-950">Social links</h2>
                  <Input
                    label="LinkedIn URL"
                    type="url"
                    name="linkedinUrl"
                    error={errors.linkedinUrl}
                    value={formData.linkedinUrl}
                    onChange={handleInputChange}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                  <Input
                    label="GitHub URL"
                    type="url"
                    name="githubUrl"
                    error={errors.githubUrl}
                    value={formData.githubUrl}
                    onChange={handleInputChange}
                    placeholder="https://github.com/yourusername"
                  />
                  <Input
                    label="Portfolio URL"
                    type="url"
                    name="portfolioUrl"
                    error={errors.portfolioUrl}
                    value={formData.portfolioUrl}
                    onChange={handleInputChange}
                    placeholder="https://yourportfolio.com"
                  />
                </>
              )}
            </Card>
          )}

          {activeTab === 'academic' && isStudentProfile && (
            <Card className="space-y-5">
              {academicLocked && (
                <p className="flex items-start gap-2 rounded-xl border border-india-600/30 bg-india-50 px-4 py-3 text-sm text-ink-800">
                  <ShieldCheckIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-india-600" />
                  <span>
                    Verified by your placement cell. Your academic details are locked; ask your
                    placement cell to unlock them if something needs correcting.
                  </span>
                </p>
              )}
              {lockedFieldsHit.length > 0 && (
                <p role="alert" className="rounded-xl border border-saffron-600/40 bg-saffron-50 px-4 py-3 text-sm text-ink-800">
                  Nothing was saved, because these are locked after verification:{' '}
                  {lockedFieldsHit.map((f) => FIELD_LABELS[f] || f).join(', ')}. Change them back, or
                  ask your placement cell to unlock your profile.
                </p>
              )}
              {!isSchoolStudent && (
                <Input
                  label="Roll number"
                  name="studentId"
                  placeholder="e.g. 22CSE031"
                  help="As printed on your college ID card. Companies shortlist by it."
                  error={errors.studentId}
                  disabled={academicLocked}
                  value={formData.studentId}
                  onChange={handleInputChange}
                />
              )}
              <div className="grid gap-5 sm:grid-cols-2">
                <Input
                  label={isSchoolStudent ? 'Stream' : 'Course'}
                  name="course"
                  error={errors.course}
                  disabled={academicLocked}
                  value={formData.course}
                  onChange={handleInputChange}
                  placeholder={
                    isSchoolStudent ? 'e.g. Science (PCM), for Class 11 and 12' : 'e.g. Bachelor of Technology'
                  }
                />
                {!isSchoolStudent && (
                  <Input
                    label="Branch / specialization"
                    name="branch"
                    error={errors.branch}
                    disabled={academicLocked}
                    value={formData.branch}
                    onChange={handleInputChange}
                    placeholder="e.g. Computer Science Engineering"
                  />
                )}
              </div>
              <div className="grid gap-5 sm:grid-cols-3">
                <Select
                  label={isSchoolStudent ? 'Class' : 'Year of study'}
                  name="yearOfStudy"
                  error={errors.yearOfStudy}
                  disabled={academicLocked}
                  value={formData.yearOfStudy}
                  onChange={handleInputChange}
                  options={yearOptions}
                />
                {!isSchoolStudent && (
                  <>
                    <Input
                      label="Graduation year"
                      type="number"
                      min="2020"
                      max="2030"
                      name="graduationYear"
                      error={errors.graduationYear}
                      disabled={academicLocked}
                      value={formData.graduationYear}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="CGPA"
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      max="10"
                      placeholder="e.g. 8.5"
                      name="cgpa"
                      error={errors.cgpa}
                      disabled={academicLocked}
                      value={formData.cgpa}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="Active backlogs"
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      name="activeBacklogs"
                      error={errors.activeBacklogs}
                      disabled={academicLocked}
                      value={formData.activeBacklogs}
                      onChange={handleInputChange}
                    />
                  </>
                )}
                <Input
                  label="Class X %"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  name="class10Percentage"
                  error={errors.class10Percentage}
                  disabled={academicLocked}
                  value={formData.class10Percentage}
                  onChange={handleInputChange}
                />
                {!isSchoolStudent && (
                  <>
                    <Input
                      label="Class XII %"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      name="class12Percentage"
                      error={errors.class12Percentage}
                      disabled={academicLocked}
                      value={formData.class12Percentage}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="Diploma % (if lateral entry)"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      name="diplomaPercentage"
                      error={errors.diplomaPercentage}
                      disabled={academicLocked}
                      value={formData.diplomaPercentage}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="Gap in education (years)"
                      type="number"
                      step="1"
                      min="0"
                      max="20"
                      name="educationGapYears"
                      error={errors.educationGapYears}
                      disabled={academicLocked}
                      value={formData.educationGapYears}
                      onChange={handleInputChange}
                    />
                  </>
                )}
              </div>

              <div>
                <p className="mb-1.5 text-sm font-medium text-ink-800">Skills</p>
                {formData.skills.length > 0 && (
                  <ul className="mb-3 flex flex-wrap gap-2">
                    {formData.skills.map((skill) => (
                      <li key={skill}>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-ink-950/15 bg-bone-100 py-0.5 pl-3 pr-1 text-sm text-ink-800">
                          {skill}
                          <IconButton
                            size="sm"
                            icon={XMarkIcon}
                            label={`Remove ${skill}`}
                            onClick={() => handleRemoveSkill(skill)}
                          />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex items-end gap-2">
                  <Input
                    label="Add a skill"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    placeholder="e.g. React"
                    onKeyDown={(e) => {
                      // Enter inside a field would otherwise submit the whole
                      // profile form instead of adding the skill.
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSkill();
                      }
                    }}
                  />
                  <Button type="button" variant="secondary" onClick={handleAddSkill}>
                    Add
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {(activeTab === 'achievements' || activeTab === 'resume') && (
            <Card>
              <p className="text-sm text-ink-600">
                {activeTab === 'achievements'
                  ? 'Achievements are managed outside edit mode — cancel editing to add or remove them.'
                  : 'Your resume is generated from this profile. Cancel editing to view or download it.'}
              </p>
            </Card>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsEditing(false);
                setErrors({});
              }}
            >
              Cancel
            </Button>
            <Button type="submit" loading={isSaving}>
              Save changes
            </Button>
          </div>
        </form>
      ) : (
        <>
          {activeTab === 'personal' && (
            <Card>
              <SectionBlock title="Basic information" className="mb-0">
                <dl>
                  <DetailRow label="Full name">{fullName || '—'}</DetailRow>
                  <DetailRow label="Email">{profile?.email}</DetailRow>
                  <DetailRow label="Phone">{profile?.phone || 'Not provided'}</DetailRow>
                  {isStudentProfile && studentProfile && (
                    <>
                      <DetailRow label="Date of birth">
                        {studentProfile.dateOfBirth
                          ? new Date(studentProfile.dateOfBirth).toLocaleDateString()
                          : 'Not provided'}
                      </DetailRow>
                      <DetailRow label="Gender">
                        {studentProfile.gender ? <span className="capitalize">{studentProfile.gender}</span> : 'Not provided'}
                      </DetailRow>
                      <DetailRow label="Address">
                        {studentProfile.address || 'Not provided'}
                      </DetailRow>
                      <DetailRow label="Bio">{studentProfile.bio || 'No bio added yet'}</DetailRow>
                    </>
                  )}
                </dl>
              </SectionBlock>

              {isStudentProfile && studentProfile && (
                <>
                  <Divider className="my-5" />
                  <h2 className="font-display text-base font-bold text-ink-950">Social links</h2>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {[
                      { label: 'LinkedIn', href: studentProfile.linkedinUrl },
                      { label: 'GitHub', href: studentProfile.githubUrl },
                      { label: 'Portfolio', href: studentProfile.portfolioUrl }
                    ]
                      .filter((link) => link.href)
                      .map((link) => (
                        <Button
                          key={link.label}
                          as="a"
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="sm"
                          variant="secondary"
                          iconRight={ArrowTopRightOnSquareIcon}
                        >
                          {link.label}
                        </Button>
                      ))}
                    {!studentProfile.linkedinUrl &&
                      !studentProfile.githubUrl &&
                      !studentProfile.portfolioUrl && (
                        <p className="text-sm text-ink-500">No links added yet.</p>
                      )}
                  </div>
                </>
              )}
            </Card>
          )}

          {activeTab === 'academic' && isStudentProfile && (
            <Card>
              <dl>
                {!isSchoolStudent && (
                  <DetailRow label="Roll number">
                    {studentProfile?.studentId || 'Not provided'}
                  </DetailRow>
                )}
                <DetailRow label={isSchoolStudent ? 'Stream' : 'Course'}>
                  {studentProfile?.course || 'Not provided'}
                </DetailRow>
                {!isSchoolStudent && (
                  <DetailRow label="Branch">{studentProfile?.branch || 'Not provided'}</DetailRow>
                )}
                <DetailRow label={isSchoolStudent ? 'Class' : 'Year of study'}>
                  {studentProfile?.yearOfStudy
                    ? yearOptions.find((o) => o.value === String(studentProfile.yearOfStudy))?.label ||
                      `Year ${studentProfile.yearOfStudy}`
                    : 'Not provided'}
                </DetailRow>
                {!isSchoolStudent && (
                  <>
                    <DetailRow label="Graduation year">
                      {studentProfile?.graduationYear || 'Not provided'}
                    </DetailRow>
                    <DetailRow label="CGPA">{studentProfile?.cgpa || 'Not provided'}</DetailRow>
                    {/* The fields companies screen on. They are what the placement
                        cell verifies and locks, so the student has to be able to
                        see them without opening the edit form. */}
                    <DetailRow label="Active backlogs">
                      {studentProfile?.activeBacklogs ?? 'Not provided'}
                    </DetailRow>
                  </>
                )}
                <DetailRow label="Class X">
                  {studentProfile?.class10Percentage != null ? `${Number(studentProfile.class10Percentage)}%` : 'Not provided'}
                </DetailRow>
                {!isSchoolStudent && (
                  <>
                    <DetailRow label="Class XII">
                      {studentProfile?.class12Percentage != null ? `${Number(studentProfile.class12Percentage)}%` : 'Not provided'}
                    </DetailRow>
                    {studentProfile?.diplomaPercentage != null && (
                      <DetailRow label="Diploma">{`${Number(studentProfile.diplomaPercentage)}%`}</DetailRow>
                    )}
                    <DetailRow label="Gap in education">
                      {studentProfile?.educationGapYears != null
                        ? studentProfile.educationGapYears === 0
                          ? 'None'
                          : `${studentProfile.educationGapYears} year${studentProfile.educationGapYears === 1 ? '' : 's'}`
                        : 'Not provided'}
                    </DetailRow>
                  </>
                )}
                {studentProfile?.percentage && (
                  <DetailRow label="Percentage">{`${studentProfile.percentage}%`}</DetailRow>
                )}
                <DetailRow label="Skills">
                  {studentProfile?.skills?.length > 0 ? (
                    <ul className="flex flex-wrap gap-2">
                      {studentProfile.skills.map((skill) => (
                        <li key={skill}>
                          <Badge tone="neutral">{skill}</Badge>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    'No skills added yet'
                  )}
                </DetailRow>
              </dl>
            </Card>
          )}

          {activeTab === 'achievements' && isStudentProfile && (
            <>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-display text-lg font-bold tracking-tight text-ink-950">
                  Achievements
                </h2>
                {!isViewingOtherUser && (
                  <Button size="sm" icon={PlusIcon} onClick={() => setShowAchievementModal(true)}>
                    Add achievement
                  </Button>
                )}
              </div>

              {achievements.length > 0 ? (
                <ul className="space-y-3">
                  {achievements.map((achievement) => (
                    <li key={achievement.id}>
                      <Card>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-display text-base font-bold text-ink-950">
                                {achievement.title}
                              </h3>
                              <Badge
                                tone={
                                  ACHIEVEMENT_TONES[achievement.achievementType] ||
                                  ACHIEVEMENT_TONES.other
                                }
                              >
                                {achievement.achievementType}
                              </Badge>
                            </div>
                            {achievement.issuingOrganization && (
                              <p className="mt-1 flex items-center gap-1.5 text-sm text-ink-600">
                                <BriefcaseIcon aria-hidden="true" className="h-4 w-4 text-ink-500" />
                                {achievement.issuingOrganization}
                              </p>
                            )}
                            {achievement.description && (
                              <p className="mt-2 text-sm text-ink-700">{achievement.description}</p>
                            )}
                            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-ink-500">
                              {achievement.issueDate && (
                                <span>
                                  Issued {new Date(achievement.issueDate).toLocaleDateString()}
                                </span>
                              )}
                              {achievement.credentialUrl && (
                                <a
                                  href={achievement.credentialUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-ink-800 underline underline-offset-2 hover:text-ink-950"
                                >
                                  View credential
                                </a>
                              )}
                            </div>
                          </div>
                          {!isViewingOtherUser && (
                            <IconButton
                              icon={TrashIcon}
                              variant="danger"
                              size="sm"
                              label={`Delete ${achievement.title}`}
                              onClick={() => setDeletingAchievement(achievement)}
                            />
                          )}
                        </div>
                      </Card>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState
                  icon={TrophyIcon}
                  title="No achievements yet"
                  description={
                    isViewingOtherUser
                      ? 'This student has not added any achievements.'
                      : 'Add certifications, awards and projects to show recruiters what you have done.'
                  }
                  action={
                    !isViewingOtherUser ? (
                      <Button icon={PlusIcon} onClick={() => setShowAchievementModal(true)}>
                        Add achievement
                      </Button>
                    ) : (
                      <Button as={Link} to="/dashboard" variant="secondary">
                        Back to dashboard
                      </Button>
                    )
                  }
                />
              )}
            </>
          )}

          {activeTab === 'privacy' && isStudentProfile && !isViewingOtherUser && (
            <Card>
              <CardHeader
                title="Who has seen your profile"
                description="Recruiters only see students from institutions your placement cell has approved them for. Every time your profile is returned to one, it is recorded here."
              />
              {dataAccess === null ? (
                <p className="mt-4 text-sm text-ink-500">Loading…</p>
              ) : dataAccess.length === 0 ? (
                <EmptyState
                  icon={ShieldCheckIcon}
                  title="No recruiter has seen your profile yet"
                  description="When a company you are eligible for searches for candidates, that will show up here."
                />
              ) : (
                <ul className="mt-4 divide-y divide-ink-950/10">
                  {dataAccess.map((entry) => (
                    <li key={entry.id} className="flex flex-wrap items-baseline justify-between gap-2 py-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-ink-950">{entry.organization}</p>
                        <p className="text-sm text-ink-600">{entry.description}</p>
                      </div>
                      <time className="shrink-0 text-xs tabular-nums text-ink-500" dateTime={entry.at}>
                        {new Date(entry.at).toLocaleString()}
                      </time>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-4 text-xs text-ink-500">
                Showing the last 180 days. Companies are named rather than individual recruiters.
              </p>
            </Card>
          )}

          {activeTab === 'resume' && isStudentProfile && (
            <>
              {hasResume ? (
                <Card>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <span
                        aria-hidden="true"
                        className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-ink-950/15 bg-bone-100 text-ink-700"
                      >
                        <DocumentTextIcon className="h-6 w-6" strokeWidth={1.6} />
                      </span>
                      <div>
                        <p className="flex items-center gap-2 font-display text-base font-bold text-ink-950">
                          <CheckCircleIcon aria-hidden="true" className="h-5 w-5 text-india-600" />
                          Resume available
                        </p>
                        <p className="text-sm text-ink-600">
                          {studentProfile.resumeUrl?.includes('http')
                            ? 'External resume link'
                            : 'Resume file uploaded'}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {studentProfile.resumeFileId || isStoredFile(studentProfile.resumeUrl) ? (
                        <>
                          <Button
                            variant="secondary"
                            icon={DocumentTextIcon}
                            onClick={handleViewResume}
                          >
                            View resume
                          </Button>
                          <Button icon={DocumentArrowDownIcon} onClick={handleDownloadResume}>
                            Download
                          </Button>
                        </>
                      ) : (
                        <Button
                          as="a"
                          href={studentProfile.resumeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="secondary"
                          icon={DocumentTextIcon}
                        >
                          View resume
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ) : (
                <EmptyState
                  icon={DocumentTextIcon}
                  title="No resume generated"
                  description={
                    isViewingOtherUser
                      ? 'This student has not generated a resume yet.'
                      : 'Complete your profile and generate your resume so recruiters and TPOs can see it.'
                  }
                  action={
                    !isViewingOtherUser ? (
                      <Button as={Link} to="/resume" icon={DocumentTextIcon}>
                        Go to resume builder
                      </Button>
                    ) : (
                      <Button as={Link} to="/dashboard" variant="secondary">
                        Back to dashboard
                      </Button>
                    )
                  }
                />
              )}
            </>
          )}
        </>
      )}

      <Modal
        open={Boolean(deletingAchievement)}
        onClose={() => setDeletingAchievement(null)}
        size="sm"
        title="Delete this achievement?"
        description={deletingAchievement?.title}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeletingAchievement(null)}>
              Keep it
            </Button>
            <Button variant="danger" onClick={handleDeleteAchievement}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-700">
          It disappears from your profile. Regenerate your resume afterwards if it was on it.
        </p>
      </Modal>

      <Modal
        open={showAchievementModal}
        onClose={() => setShowAchievementModal(false)}
        title="Add achievement"
        description="Certifications, awards, publications and projects."
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAchievementModal(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="add-achievement"
              loading={addingAchievement}
              disabled={!newAchievement.title.trim()}
            >
              Add achievement
            </Button>
          </>
        }
      >
        <form
          id="add-achievement"
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleAddAchievement();
          }}
        >
          <Input
            label="Title"
            required
            value={newAchievement.title}
            onChange={(e) => setNewAchievement((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="Achievement title"
          />
          <Select
            label="Type"
            value={newAchievement.achievementType}
            onChange={(e) =>
              setNewAchievement((prev) => ({ ...prev, achievementType: e.target.value }))
            }
            options={[
              { value: 'academic', label: 'Academic' },
              { value: 'project', label: 'Project' },
              { value: 'certification', label: 'Certification' },
              { value: 'competition', label: 'Competition' },
              { value: 'publication', label: 'Publication' },
              { value: 'other', label: 'Other' }
            ]}
          />
          <Textarea
            label="Description"
            rows={3}
            value={newAchievement.description}
            onChange={(e) =>
              setNewAchievement((prev) => ({ ...prev, description: e.target.value }))
            }
            placeholder="Describe your achievement"
          />
          <Input
            label="Issuing organization"
            value={newAchievement.issuingOrganization}
            onChange={(e) =>
              setNewAchievement((prev) => ({ ...prev, issuingOrganization: e.target.value }))
            }
            placeholder="Organization name"
          />
          <Input
            label="Issue date"
            type="date"
            value={newAchievement.issueDate}
            onChange={(e) => setNewAchievement((prev) => ({ ...prev, issueDate: e.target.value }))}
          />
          <Input
            label="Credential URL"
            type="url"
            value={newAchievement.credentialUrl}
            onChange={(e) =>
              setNewAchievement((prev) => ({ ...prev, credentialUrl: e.target.value }))
            }
            placeholder="https://example.com/certificate"
            help="Optional. https:// is added automatically if you leave it off."
          />
        </form>
      </Modal>
    </PageShell>
  );
};

export default Profile;
